import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

const PURPLE = "#5a21d2";
const PINK = "#f72586";

const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(-50%) translateX(-6px);
	}
	to {
		opacity: 1;
		transform: translateY(-50%) translateX(0);
	}
`;

const Card = styled.div`
	position: fixed;
	width: 320px;
	max-width: min(320px, calc(100vw - 120px));
	padding: 20px;
	border-radius: 20px;
	background: ${PURPLE};
	box-shadow: 0 16px 40px rgba(42, 5, 127, 0.32);
	z-index: 3200;
	animation: ${fadeIn} 220ms ease-out;
	pointer-events: auto;
	transform: translateY(-50%);

	&::before {
		content: "";
		position: absolute;
		left: -8px;
		top: 50%;
		transform: translateY(-50%);
		width: 0;
		height: 0;
		border-top: 9px solid transparent;
		border-bottom: 9px solid transparent;
		border-right: 9px solid ${PURPLE};
	}
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 10px;
`;

const Title = styled.h2`
	margin: 0;
	font-family: Poppins, sans-serif;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.3;
	color: #ffffff;
`;

const NewBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 20px;
	padding: 0 8px;
	border-radius: 999px;
	background: ${PINK};
	color: #ffffff;
	font-family: Poppins, sans-serif;
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	flex-shrink: 0;
`;

const Body = styled.p`
	margin: 0 0 18px;
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 400;
	line-height: 1.45;
	color: #ffffff;
`;

const GotItButton = styled.button`
	border: none;
	border-radius: 999px;
	background: #ffffff;
	color: ${PURPLE};
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 700;
	line-height: 1;
	padding: 10px 18px;
	cursor: pointer;

	&:hover {
		background: #f6f4ff;
	}

	&:focus-visible {
		outline: 2px solid #ffffff;
		outline-offset: 2px;
	}
`;

interface NotificationFeatureIntroProps {
	anchorEl: HTMLElement | null;
	onDismiss: () => void;
}

export function NotificationFeatureIntro({ anchorEl, onDismiss }: NotificationFeatureIntroProps) {
	const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

	useEffect(() => {
		if (!anchorEl) {
			setCoords(null);
			return;
		}

		const update = () => {
			const rect = anchorEl.getBoundingClientRect();
			setCoords({
				top: rect.top + rect.height / 2,
				left: rect.right + 14,
			});
		};

		update();
		window.addEventListener("resize", update);
		window.addEventListener("scroll", update, true);
		return () => {
			window.removeEventListener("resize", update);
			window.removeEventListener("scroll", update, true);
		};
	}, [anchorEl]);

	if (typeof document === "undefined" || !anchorEl || !coords) return null;

	return createPortal(
		<Card
			role="dialog"
			aria-labelledby="notification-feature-intro-title"
			aria-describedby="notification-feature-intro-body"
			style={{ top: coords.top, left: coords.left }}
		>
			<Header>
				<Title id="notification-feature-intro-title">Notification System</Title>
				<NewBadge>NEW</NewBadge>
			</Header>
			<Body id="notification-feature-intro-body">
				Keep users informed in real time with smart, customizable notifications that deliver timely updates
				in-app—so nothing important gets missed.
			</Body>
			<GotItButton type="button" onClick={onDismiss}>
				Got it!
			</GotItButton>
		</Card>,
		document.body,
	);
}
